import { PrimitiveAtom, atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useMemo } from 'react';
import type { Getter, Setter } from 'jotai';
import { WELL_STATUS_SLOT_CATEGORY_KEYS } from 'utils/slot-categories';
import type { SlotCategoryKey } from 'utils/slot-categories';

export type LayerAtom = typeof assetsLayerAtom;
export type AllLayersAtom = ReturnType<typeof useCreateAllLayersAtom>;

const assetsLayerAtom = atomWithStorage('package-map-assets-layer', true);
const developmentAreaLayerAtom = atomWithStorage(
  'package-map-development-area-layer',
  true
);
const titleLayerAtom = atomWithStorage('package-map-title-layer', true);
const verticalWellsLayerAtom = atomWithStorage(
  'package-vertical-well-layer',
  true
);

const horizontalProducingWellsLayerAtom = atomWithStorage(
  'package-horizontal-producing-well-layer',
  true
);

const horizontalDUCWellsLayerAtom = atomWithStorage(
  'package-horizontal-duc-well-layer',
  true
);

const horizontalPermitWellsLayerAtom = atomWithStorage(
  'package-horizontal-permit-well-layer',
  true
);

const allLayersBaseAtom = atomWithStorage(
  'package-scoped-layers-all-checked',
  true
);

type Direction = 'horizontal' | 'vertical';

/** One toggle atom per Create New Well status, keyed by the status, in display order. */
export type NonDerivedLayerAtoms = Readonly<
  Partial<Record<SlotCategoryKey, PrimitiveAtom<boolean>>>
>;

/**
 * One stored toggle per Create New Well status for a direction, each on until switched off.
 *
 * @param {Direction} direction - `horizontal` or `vertical`.
 * @return {NonDerivedLayerAtoms} The toggle atom per status.
 */
const nonDerivedLayerAtoms = (direction: Direction): NonDerivedLayerAtoms =>
  Object.fromEntries(
    WELL_STATUS_SLOT_CATEGORY_KEYS.map((status) => [
      status,
      atomWithStorage(
        `package-wells-v2-${direction}-${status.replace('_', '-')}-layer`,
        true
      ),
    ])
  );

/**
 * @param {Getter} get - The jotai getter.
 * @param {NonDerivedLayerAtoms} atoms - A direction's toggle atoms.
 * @return {boolean[]} Each status's toggle, in display order.
 */
const nonDerivedStates = (get: Getter, atoms: NonDerivedLayerAtoms) =>
  WELL_STATUS_SLOT_CATEGORY_KEYS.map((status) => get(atoms[status]!));

/**
 * @param {Getter} get - The jotai getter.
 * @param {NonDerivedLayerAtoms} atoms - A direction's toggle atoms.
 * @return {boolean} True when any of the four is on.
 */
const nonDerivedChecked = (get: Getter, atoms: NonDerivedLayerAtoms) =>
  nonDerivedStates(get, atoms).some(Boolean);

/**
 * Switches all four of a direction's Create New Well toggles at once.
 *
 * @param {Setter} set - The jotai setter.
 * @param {NonDerivedLayerAtoms} atoms - A direction's toggle atoms.
 * @param {boolean} checked - The state to set.
 */
const setNonDerived = (
  set: Setter,
  atoms: NonDerivedLayerAtoms,
  checked: boolean
) => {
  WELL_STATUS_SLOT_CATEGORY_KEYS.forEach((status) =>
    set(atoms[status]!, checked)
  );
};

// Wells V2 atoms - use a pure storage atom for the main state
const wellsV2LayerAtom = atomWithStorage<
  'CHECKED' | 'UNCHECKED' | 'INDETERMINATE'
>('package-wells-v2-layer', 'CHECKED');

// Horizontal Wells V2 atoms - use a pure storage atom for horizontal
const wellsV2HorizontalLayerAtom = atomWithStorage<
  'CHECKED' | 'UNCHECKED' | 'INDETERMINATE'
>('package-wells-v2-horizontal-layer', 'CHECKED');

// Status atoms for horizontal
const wellsV2HorizontalProducingLayerAtom = atomWithStorage(
  'package-wells-v2-horizontal-producing-layer',
  true
);

const wellsV2HorizontalDucLayerAtom = atomWithStorage(
  'package-wells-v2-horizontal-duc-layer',
  true
);

const wellsV2HorizontalPermitLayerAtom = atomWithStorage(
  'package-wells-v2-horizontal-permit-layer',
  true
);

const wellsV2HorizontalShutInLayerAtom = atomWithStorage(
  'package-wells-v2-horizontal-shut-in-layer',
  true
);

const wellsV2HorizontalExpiredPermitLayerAtom = atomWithStorage(
  'package-wells-v2-horizontal-expired-permit-layer',
  true
);

const wellsV2HorizontalInactiveLayerAtom = atomWithStorage(
  'package-wells-v2-horizontal-inactive-layer',
  true
);

// The four Create New Well statuses, each its own toggle (INS-13300), in display order.
const wellsV2HorizontalNonDerivedLayerAtoms =
  nonDerivedLayerAtoms('horizontal');

// Vertical Wells V2 atoms - use a pure storage atom for vertical
const wellsV2VerticalLayerAtom = atomWithStorage<
  'CHECKED' | 'UNCHECKED' | 'INDETERMINATE'
>('package-wells-v2-vertical-layer', 'CHECKED');

// Status atoms for vertical
const wellsV2VerticalProducingLayerAtom = atomWithStorage(
  'package-wells-v2-vertical-producing-layer',
  true
);

const wellsV2VerticalDucLayerAtom = atomWithStorage(
  'package-wells-v2-vertical-duc-layer',
  true
);

const wellsV2VerticalPermitLayerAtom = atomWithStorage(
  'package-wells-v2-vertical-permit-layer',
  true
);

const wellsV2VerticalShutInLayerAtom = atomWithStorage(
  'package-wells-v2-vertical-shut-in-layer',
  true
);

const wellsV2VerticalExpiredPermitLayerAtom = atomWithStorage(
  'package-wells-v2-vertical-expired-permit-layer',
  true
);

const wellsV2VerticalInactiveLayerAtom = atomWithStorage(
  'package-wells-v2-vertical-inactive-layer',
  true
);

// The four Create New Well statuses, each its own toggle (INS-13300), in display order.
const wellsV2VerticalNonDerivedLayerAtoms = nonDerivedLayerAtoms('vertical');

// The Create New Well statuses whose toggle is on, per direction, in display order: the wells
// layer draws exactly these, each in its own colour.
const wellsV2HorizontalNonDerivedEnabledAtom = atom((get) =>
  WELL_STATUS_SLOT_CATEGORY_KEYS.filter((status) =>
    get(wellsV2HorizontalNonDerivedLayerAtoms[status]!)
  )
);
const wellsV2VerticalNonDerivedEnabledAtom = atom((get) =>
  WELL_STATUS_SLOT_CATEGORY_KEYS.filter((status) =>
    get(wellsV2VerticalNonDerivedLayerAtoms[status]!)
  )
);

// Add a derived atom to track the state of all children and sync with parent
const isAnyWellsV2CategoryEnabledAtom = atom((get) => {
  // When any horizontal or vertical child is checked, the parent should be checked
  const isHorizontalAnyChildChecked =
    get(wellsV2HorizontalProducingLayerAtom) ||
    get(wellsV2HorizontalDucLayerAtom) ||
    get(wellsV2HorizontalPermitLayerAtom) ||
    get(wellsV2HorizontalShutInLayerAtom) ||
    get(wellsV2HorizontalExpiredPermitLayerAtom) ||
    get(wellsV2HorizontalInactiveLayerAtom) ||
    nonDerivedChecked(get, wellsV2HorizontalNonDerivedLayerAtoms);

  const isVerticalAnyChildChecked =
    get(wellsV2VerticalProducingLayerAtom) ||
    get(wellsV2VerticalDucLayerAtom) ||
    get(wellsV2VerticalPermitLayerAtom) ||
    get(wellsV2VerticalShutInLayerAtom) ||
    get(wellsV2VerticalExpiredPermitLayerAtom) ||
    get(wellsV2VerticalInactiveLayerAtom) ||
    nonDerivedChecked(get, wellsV2VerticalNonDerivedLayerAtoms);

  // If any child is checked, return true
  return isHorizontalAnyChildChecked || isVerticalAnyChildChecked;
});

// Add more derived atoms to track indeterminate states
export const isWellsV2HorizontalIndeterminateAtom = atom((get) => {
  const horizontalStates = [
    get(wellsV2HorizontalProducingLayerAtom),
    get(wellsV2HorizontalDucLayerAtom),
    get(wellsV2HorizontalPermitLayerAtom),
    get(wellsV2HorizontalShutInLayerAtom),
    get(wellsV2HorizontalExpiredPermitLayerAtom),
    get(wellsV2HorizontalInactiveLayerAtom),
    ...nonDerivedStates(get, wellsV2HorizontalNonDerivedLayerAtoms),
  ];

  const someChecked = horizontalStates.some((state) => state);
  const allChecked = horizontalStates.every((state) => state);

  // Return true when some but not all children are checked
  return someChecked && !allChecked;
});

export const isWellsV2VerticalIndeterminateAtom = atom((get) => {
  const verticalStates = [
    get(wellsV2VerticalProducingLayerAtom),
    get(wellsV2VerticalDucLayerAtom),
    get(wellsV2VerticalPermitLayerAtom),
    get(wellsV2VerticalShutInLayerAtom),
    get(wellsV2VerticalExpiredPermitLayerAtom),
    get(wellsV2VerticalInactiveLayerAtom),
    ...nonDerivedStates(get, wellsV2VerticalNonDerivedLayerAtoms),
  ];

  const someChecked = verticalStates.some((state) => state);
  const allChecked = verticalStates.every((state) => state);

  // Return true when some but not all children are checked
  return someChecked && !allChecked;
});

export const isWellsV2IndeterminateAtom = atom((get) => {
  // First check if horizontal and vertical are in different states
  const horizontalState = get(wellsV2HorizontalLayerAtom);
  const verticalState = get(wellsV2VerticalLayerAtom);

  if (horizontalState !== verticalState) {
    return true;
  }

  // Then check if either horizontal or vertical is indeterminate
  const isHorizontalIndeterminate = get(isWellsV2HorizontalIndeterminateAtom);
  const isVerticalIndeterminate = get(isWellsV2VerticalIndeterminateAtom);

  return isHorizontalIndeterminate || isVerticalIndeterminate;
});

// Replace the existing syncWellsV2WithChildrenAtom with this more comprehensive version
const syncWellsV2WithChildrenAtom = atom(null, (get, set) => {
  // 1. Determine the state of horizontal category
  const horizontalStates = [
    get(wellsV2HorizontalProducingLayerAtom),
    get(wellsV2HorizontalDucLayerAtom),
    get(wellsV2HorizontalPermitLayerAtom),
    get(wellsV2HorizontalShutInLayerAtom),
    get(wellsV2HorizontalExpiredPermitLayerAtom),
    get(wellsV2HorizontalInactiveLayerAtom),
    ...nonDerivedStates(get, wellsV2HorizontalNonDerivedLayerAtoms),
  ];

  const horizontalAllChecked = horizontalStates.every((state) => state);
  const horizontalSomeChecked = horizontalStates.some((state) => state);
  set(
    wellsV2HorizontalLayerAtom,
    horizontalAllChecked
      ? 'CHECKED'
      : horizontalSomeChecked
        ? 'INDETERMINATE'
        : 'UNCHECKED'
  );

  const verticalStates = [
    get(wellsV2VerticalProducingLayerAtom),
    get(wellsV2VerticalDucLayerAtom),
    get(wellsV2VerticalPermitLayerAtom),
    get(wellsV2VerticalShutInLayerAtom),
    get(wellsV2VerticalExpiredPermitLayerAtom),
    get(wellsV2VerticalInactiveLayerAtom),
    ...nonDerivedStates(get, wellsV2VerticalNonDerivedLayerAtoms),
  ];

  const verticalAllChecked = verticalStates.every((state) => state);
  const verticalSomeChecked = verticalStates.some((state) => state);
  set(
    wellsV2VerticalLayerAtom,
    verticalAllChecked
      ? 'CHECKED'
      : verticalSomeChecked
        ? 'INDETERMINATE'
        : 'UNCHECKED'
  );

  // Get the updated category states after possible changes above
  const updatedHorizontalState = get(wellsV2HorizontalLayerAtom);
  const updatedVerticalState = get(wellsV2VerticalLayerAtom);

  // Check if both categories are now checked (after our updates above)
  const bothCategoriesChecked =
    updatedHorizontalState === 'CHECKED' && updatedVerticalState === 'CHECKED';
  const nothingChecked =
    updatedHorizontalState === 'UNCHECKED' &&
    updatedVerticalState === 'UNCHECKED';

  // Updated logic for parent checkbox state
  set(
    wellsV2LayerAtom,
    bothCategoriesChecked
      ? 'CHECKED'
      : nothingChecked
        ? 'UNCHECKED'
        : 'INDETERMINATE'
  );
});

// 1. A setter atom that updates all child status checkboxes when the horizontal category changes
const setWellsV2HorizontalAndChildrenAtom = atom(
  null, // read is handled by the original atoms
  (get, set, checked: boolean) => {
    // Update horizontal category
    set(wellsV2HorizontalLayerAtom, checked ? 'CHECKED' : 'UNCHECKED');

    // Update all horizontal status filters
    set(wellsV2HorizontalProducingLayerAtom, checked);
    set(wellsV2HorizontalDucLayerAtom, checked);
    set(wellsV2HorizontalPermitLayerAtom, checked);
    set(wellsV2HorizontalShutInLayerAtom, checked);
    set(wellsV2HorizontalExpiredPermitLayerAtom, checked);
    set(wellsV2HorizontalInactiveLayerAtom, checked);
    setNonDerived(set, wellsV2HorizontalNonDerivedLayerAtoms, checked);

    // Sync the parent Wells Beta checkbox with children
    set(syncWellsV2WithChildrenAtom);
  }
);

// 2. A setter atom that updates all child status checkboxes when the vertical category changes
const setWellsV2VerticalAndChildrenAtom = atom(
  null, // read is handled by the original atoms
  (get, set, checked: boolean) => {
    // Update vertical category
    set(wellsV2VerticalLayerAtom, checked ? 'CHECKED' : 'UNCHECKED');

    // Update all vertical status filters
    set(wellsV2VerticalProducingLayerAtom, checked);
    set(wellsV2VerticalDucLayerAtom, checked);
    set(wellsV2VerticalPermitLayerAtom, checked);
    set(wellsV2VerticalShutInLayerAtom, checked);
    set(wellsV2VerticalExpiredPermitLayerAtom, checked);
    set(wellsV2VerticalInactiveLayerAtom, checked);
    setNonDerived(set, wellsV2VerticalNonDerivedLayerAtoms, checked);

    // Sync the parent Wells Beta checkbox with children
    set(syncWellsV2WithChildrenAtom);
  }
);

// 3. A setter atom that updates all checkboxes when the main Wells Beta checkbox changes
const setWellsV2AndAllChildrenAtom = atom(
  null, // read is handled by the original atoms
  (get, set, checked: boolean) => {
    // First, directly update the main checkbox to ensure visual feedback
    set(wellsV2LayerAtom, checked ? 'CHECKED' : 'UNCHECKED');

    // Then update horizontal and vertical categories
    set(wellsV2HorizontalLayerAtom, checked ? 'CHECKED' : 'UNCHECKED');
    set(wellsV2VerticalLayerAtom, checked ? 'CHECKED' : 'UNCHECKED');

    // Update all horizontal status filters
    set(wellsV2HorizontalProducingLayerAtom, checked);
    set(wellsV2HorizontalDucLayerAtom, checked);
    set(wellsV2HorizontalPermitLayerAtom, checked);
    set(wellsV2HorizontalShutInLayerAtom, checked);
    set(wellsV2HorizontalExpiredPermitLayerAtom, checked);
    set(wellsV2HorizontalInactiveLayerAtom, checked);
    setNonDerived(set, wellsV2HorizontalNonDerivedLayerAtoms, checked);

    // Update all vertical status filters
    set(wellsV2VerticalProducingLayerAtom, checked);
    set(wellsV2VerticalDucLayerAtom, checked);
    set(wellsV2VerticalPermitLayerAtom, checked);
    set(wellsV2VerticalShutInLayerAtom, checked);
    set(wellsV2VerticalExpiredPermitLayerAtom, checked);
    set(wellsV2VerticalInactiveLayerAtom, checked);
    setNonDerived(set, wellsV2VerticalNonDerivedLayerAtoms, checked);
  }
);

// Returns an atom used to check multiple layers at the same time.
// The atom used by the "check all checkbox" is configurable with the
// hook argument.
// The returned atom accepts the checkbox value (boolean) and an optional
// set of boolean atoms that should be checked as well.
// This is useful for example, to pass atoms that remember state in localstorage
// or regular atoms, depending on the usecase.
const useCreateAllLayersAtom = (allLayersAtom: PrimitiveAtom<boolean>) => {
  return useMemo(
    () =>
      atom<boolean, [boolean, PrimitiveAtom<boolean>[] | undefined], void>(
        (get) => get(allLayersAtom),
        (get, set, payload, atoms) => {
          set(allLayersAtom, payload);
          if (atoms) {
            for (const layer of atoms) {
              set(layer, payload);
            }
          }
          set(verticalWellsLayerAtom, payload);
          set(horizontalDUCWellsLayerAtom, payload);
          set(horizontalPermitWellsLayerAtom, payload);
          set(horizontalProducingWellsLayerAtom, payload);
        }
      ),
    [allLayersAtom]
  );
};

export {
  useCreateAllLayersAtom,
  allLayersBaseAtom,
  assetsLayerAtom,
  developmentAreaLayerAtom,
  horizontalDUCWellsLayerAtom,
  horizontalPermitWellsLayerAtom,
  horizontalProducingWellsLayerAtom,
  titleLayerAtom,
  verticalWellsLayerAtom,
  wellsV2LayerAtom,
  wellsV2HorizontalLayerAtom,
  wellsV2HorizontalProducingLayerAtom,
  wellsV2HorizontalDucLayerAtom,
  wellsV2HorizontalPermitLayerAtom,
  wellsV2HorizontalShutInLayerAtom,
  wellsV2HorizontalExpiredPermitLayerAtom,
  wellsV2HorizontalInactiveLayerAtom,
  wellsV2HorizontalNonDerivedLayerAtoms,
  wellsV2HorizontalNonDerivedEnabledAtom,
  wellsV2VerticalLayerAtom,
  wellsV2VerticalProducingLayerAtom,
  wellsV2VerticalDucLayerAtom,
  wellsV2VerticalPermitLayerAtom,
  wellsV2VerticalShutInLayerAtom,
  wellsV2VerticalExpiredPermitLayerAtom,
  wellsV2VerticalInactiveLayerAtom,
  wellsV2VerticalNonDerivedLayerAtoms,
  wellsV2VerticalNonDerivedEnabledAtom,
  setWellsV2HorizontalAndChildrenAtom,
  setWellsV2VerticalAndChildrenAtom,
  setWellsV2AndAllChildrenAtom,
  isAnyWellsV2CategoryEnabledAtom,
  syncWellsV2WithChildrenAtom,
};
